import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { IPatientWaitingListState } from "@/app_redux/reducers/slices/auth/patient_waiting_list_slice";
import { useEffect, useRef, useState } from "react";

import { ResponsivePie } from "@nivo/pie";

const ResponsivePieChart: React.FC<{ data: any }> = ({ data }) => {
  const containerRef = useRef();

  useEffect(() => {
    if (containerRef.current) {
      const element = containerRef.current as HTMLElement;
      const svgElement = element.getElementsByTagName("svg")[0];
      if (svgElement) {
        svgElement.classList.add("overflow-visible");
      }
    }
  }, [containerRef.current]);

  return (
    <div ref={containerRef} className="w-full h-full">
      <ResponsivePie
        data={data}
        margin={{ top: 50, bottom: 50, right: 50, left: 50 }}
        innerRadius={0.5}
        padAngle={0.7}
        cornerRadius={3}
        activeOuterRadiusOffset={8}
        borderWidth={1}
        borderColor={{
          from: "color",
          modifiers: [["darker", 0.2]],
        }}
        arcLinkLabelsSkipAngle={10}
        arcLinkLabelsTextColor="#333333"
        arcLinkLabelsThickness={2}
        arcLinkLabelsColor={{ from: "color" }}
        arcLabelsSkipAngle={10}
        arcLabelsTextColor={{
          from: "color",
          modifiers: [["darker", 2]],
        }}
      />
    </div>
  );
};

type ChartDataType = {
  id: string;
  label: string;
  value: string;
};

const PWLAnalytics = () => {
  const { getRouteKey } = useRouter();
  const { waitingList, waitingStats } = useRouteInfo(getRouteKey("PATIENT_WAITING_LIST", "id")).routeState
    .state as IPatientWaitingListState;

  const [_petSpeciesAnalyticsData, setPetSpeciesAnalyticsData] = useState<ChartDataType[]>([]);

  const [appointmentCountAnalyticsData, setAppointmentCountAnalyticsData] = useState<ChartDataType[]>([]);
  const [servicesAnalyticsData, setServicesAnalyticsData] = useState<ChartDataType[]>([]);
  const [customerTypeAnalyticsData, setCustomerTypeAnalyticsData] = useState<ChartDataType[]>([]);

  const analyzePatientStatus = (): ChartDataType[] => {
    const reducedPatientData = waitingList.reduce(
      (final, current) => {
        if (current?.is_member) {
          if (current.is_member?.toLocaleLowerCase() === "member") {
            final.Member = final.Member + 1;
          } else {
            final.Non_Member = final.Non_Member + 1;
          }
        } else {
          final.None = final.None + 1;
        }
        return final;
      },
      { Non_Member: 0, Member: 0, None: 0 },
    );
    return Object.keys(reducedPatientData).map((key) => {
      return {
        id: key,
        label: key,
        value: `${reducedPatientData[key]}`,
      };
    });
  };

  const analyzeServies = (): ChartDataType[] => {
    const reducedServices = waitingList.reduce(
      (final, current) => {
        if (current.subservice_details) {
          if (final[current?.subservice_details?.name]) {
            final[current?.subservice_details?.name] = final[current.subservice_details.name] + 1;
          } else {
            final[current.subservice_details.name] = 1;
          }
        }

        return final;
      },
      {} as { [key: string]: number },
    );
    return Object.keys(reducedServices).map((serviceKey) => {
      return {
        id: serviceKey,
        label: serviceKey,
        value: `${reducedServices[serviceKey]}`,
      };
    });
  };

  const analyzeCountStats = () => {
    const reducedAppointmentStats: ChartDataType[] = Object.keys(waitingStats[0]).map((statKey) => {
      return { id: statKey, label: statKey, value: waitingStats[0][statKey]?.count };
    });
    return reducedAppointmentStats;
  };

  const analyzePetSpecies = () => {
    const reducedPetSpecies = waitingList.reduce(
      (final, current) => {
        if (current?.pet_details?.pet_species_type_id === "2") {
          final.cats = final.cats + 1;
        } else if (current?.pet_details?.pet_species_type_id === "1") {
          final.dogs = final.dogs + 1;
        } else if (current?.pet_details?.pet_species_type_id === "3") {
          final.others = final.others + 1;
        } else {
          final.others = final.others + 1;
        }
        return final;
      },
      { cats: 0, dogs: 0, others: 0 },
    );
    return Object.keys(reducedPetSpecies).map((key) => {
      return { id: key, label: key, value: reducedPetSpecies[key] };
    });
  };

  useEffect(() => {
    if (waitingStats.length === 0 || !waitingStats) return;
    setAppointmentCountAnalyticsData(analyzeCountStats());
  }, [waitingStats]);

  useEffect(() => {
    if (waitingList?.length === 0 || !waitingList) return;
    setServicesAnalyticsData(analyzeServies());
    setPetSpeciesAnalyticsData(analyzePetSpecies());
    setCustomerTypeAnalyticsData(analyzePatientStatus());
  }, [waitingList]);
  return (
    <div className="grid grid-cols-3 gap-2">
      <div className="h-72 w-72 ml-10  overflow-visible flex flex-col justify-center items-center ">
        <ResponsivePieChart data={appointmentCountAnalyticsData} />
        <p className="font-semibold">Appointments</p>
      </div>
      <div className="h-72 w-72 overflow-visible flex flex-col justify-center items-center ">
        <ResponsivePieChart data={customerTypeAnalyticsData} />
        <p className="font-semibold">Customer Type</p>
      </div>
      <div className="h-72 w-72 overflow-visible flex flex-col justify-center items-center ">
        <ResponsivePieChart data={servicesAnalyticsData} />
        <p className="font-semibold">Services</p>
      </div>
    </div>
  );
};

export default PWLAnalytics;
